import Typography from '@mui/material/Typography';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import Autocomplete from '@mui/material/Autocomplete';
import {isString} from 'lodash/fp';
import {TextField, filledInputClasses} from '@mui/material';
import {useId} from 'react';
import {styled} from '@mui/material/styles';

import {UseStoredFilters, UseStoredFiltersActions} from 'filters/features/types';

import {yup} from 'utils/yup';
import {DEFAULT_FILTER_NAME} from 'core/constants/policyFilter';
import {useSourceFilter} from 'filters/contexts/FilterStackProvider';
import {useFilterActionsContext} from 'filters/contexts/FilterActionsProvider/FilterActionsProvider';

import {
  DecisionsDialog,
  DecisionsDialogActions,
  PrimaryActionButton,
  DecisionsDialogContent,
  SecondaryActionButton,
} from 'modules/decisionsNext/components/DecisionsDialog';

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required('Please name your filter before saving')
    .test('non-default', 'Please use another name', value => value !== DEFAULT_FILTER_NAME),
});

type Schema = yup.InferType<typeof schema>;

const STextField = styled(TextField)(({theme}) => ({
  [`& .${filledInputClasses.root}`]: {
    paddingRight: `${theme.spacing(8)} !important`,
  },
}));

export const SaveSelectionDialog = ({
  open,
  onClose,
  useStoredFilters,
  useStoredFiltersActions,
}: {
  open: boolean;
  onClose: () => void;
  useStoredFilters: UseStoredFilters;
  useStoredFiltersActions: UseStoredFiltersActions;
}) => {
  const id = useId();
  const filter = useSourceFilter();
  const {
    savedFilters: {saved},
  } = useStoredFilters();
  const {isSaving, saveFilter} = useStoredFiltersActions();
  const {useApplyFilter} = useFilterActionsContext();
  const applyFilter = useApplyFilter();

  const {
    control,
    formState: {errors},
    watch,
    reset,
    handleSubmit,
  } = useForm<Schema>({
    values: {
      name: '',
    },
    resolver: yupResolver(schema),
  });
  const name = watch('name');
  const isUpdate = !!saved?.find(filter => filter.name === name);

  const onSubmit = async (values: Schema) => {
    if (!filter) {
      throw new Error('No filter available to save');
    }

    try {
      const savedFilterId = await saveFilter(filter, values.name);

      if (savedFilterId) {
        await applyFilter(filter, savedFilterId);
      }

      onClose();
    } catch {
      /* decline */
    }
  };

  return (
    <DecisionsDialog
      maxWidth='xs'
      title='Save filters'
      open={open}
      onClose={onClose}
      onExited={() =>
        reset({
          name: '',
        })
      }
    >
      <DecisionsDialogContent>
        <Typography variant='subtitle1' marginBottom={6}>
          Save your filters for future easy data analysis:
        </Typography>
        <form id={id} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='name'
            render={({field}) => (
              <Autocomplete<{id: string; name: string}, false, true, true>
                freeSolo
                disabled={field.disabled}
                forcePopupIcon
                selectOnFocus
                disableClearable
                handleHomeEndKeys
                inputValue={field.value || ''}
                onInputChange={(_, value) => field.onChange(value)}
                onBlur={e => {
                  const value = (e.target as HTMLInputElement).value;

                  value && field.onChange(value);
                  field.onBlur();
                }}
                onChange={(_, value) => {
                  if (!value) {
                    return field.onChange('');
                  }

                  if (isString(value)) {
                    return field.onChange(value);
                  }

                  field.onChange(value.name);
                }}
                options={saved ?? []}
                getOptionLabel={option => (isString(option) ? option : option.name)}
                renderInput={params => (
                  <STextField
                    {...params}
                    label='Name'
                    variant='filled'
                    color='secondary'
                    error={!!errors.name?.message}
                    helperText={errors.name?.message || <span>&nbsp;</span>}
                    inputProps={{
                      ...params.inputProps,
                      maxLength: 100,
                    }}
                  />
                )}
              />
            )}
          />
        </form>
      </DecisionsDialogContent>
      <DecisionsDialogActions
        primaryAction={
          <PrimaryActionButton type='submit' form={id} loading={isSaving}>
            {isUpdate ? 'Update Filters' : 'Save Filters'}
          </PrimaryActionButton>
        }
        secondaryAction={<SecondaryActionButton onClick={onClose}>Cancel</SecondaryActionButton>}
      />
    </DecisionsDialog>
  );
};
